/* ============================================
   Configuración del pipeline — los pasos y los campos de cada quien

   Un cliente nos mandó su embudo en una planilla con veinte estados propios
   («Coti enviada», «1er Seguimiento», «Sec A1»…) y cinco columnas que el ERP
   no tenía. La mitad de eso ya existía acá con otro nombre; lo que faltaba
   era poder nombrar los pasos finos y agregar sus datos.

   Las seis etapas NO se tocan desde acá, y la pantalla lo dice: sobre ellas
   actúan el salto a Cotizado al crear una cotización, la guarda con la que
   los bots deciden si hablan, el cierre y los reportes.

   Dos pestañas y no una pantalla larga: configurar los pasos y configurar los
   campos son dos ratos distintos, y verlos juntos obligaba a bajar por trece
   inputs para llegar a lo otro.

   Cada cosa se lee como una ficha y se edita a pedido. Todo en modo edición
   —trece cajas de texto abiertas— no se lee: no se distingue lo que hay de lo
   que se está escribiendo, y un clic distraído renombra un paso.
   ============================================ */
import { useCallback, useEffect, useState } from 'react'
import { useApi } from '@/hooks/useApi'
import { showToast } from '@/components/layout/Toast'
import type { CampoDefinicion, PipelineSubestado, TipoCampo } from '@/lib/types'
import { ETAPAS_TABLERO } from '@/lib/etapas'
import {
  IconPlus, IconTrash, IconArrowUp, IconArrowDown, IconEyeOff, IconEye, IconEdit, IconCheck, IconX,
} from '@/components/icons/Icons'

/* Los colores son TOKENS del design system, no hex: cada uno tiene su valor
   en el tema claro y en el oscuro, así que un paso pintado hoy se sigue
   leyendo cuando el panel cambia de tema. */
const COLORES = ['cat-1', 'cat-2', 'cat-3', 'cat-4', 'cat-5', 'cat-6', 'cat-7', 'cat-8'] as const
type Color = (typeof COLORES)[number]

const TIPOS: { valor: TipoCampo; label: string }[] = [
  { valor: 'texto', label: 'Texto' },
  { valor: 'lista', label: 'Lista' },
  { valor: 'numero', label: 'Número' },
  { valor: 'moneda', label: 'Importe' },
  { valor: 'fecha', label: 'Fecha' },
  { valor: 'booleano', label: 'Sí / No' },
]

type Pestana = 'pasos' | 'campos'

/** Los ocho colores, más «sin color» (hereda el de la etapa). */
function Paleta({ valor, onElegir }: { valor: string | null; onElegir: (c: Color | null) => void }) {
  return (
    <div className="cfg-paleta">
      <button
        type="button"
        className={`cfg-paleta__color cfg-paleta__color--ninguno${valor ? '' : ' is-elegido'}`}
        onClick={() => onElegir(null)}
        title="Sin color propio (usa el de la etapa)"
      />
      {COLORES.map(c => (
        <button
          key={c}
          type="button"
          className={`cfg-paleta__color${valor === c ? ' is-elegido' : ''}`}
          style={{ background: `var(--${c})` }}
          onClick={() => onElegir(c)}
          title={c}
        />
      ))}
    </div>
  )
}

export default function PartnerConfigPipeline() {
  const api = useApi()
  const [pestana, setPestana] = useState<Pestana>('pasos')
  const [pasos, setPasos] = useState<PipelineSubestado[]>([])
  const [campos, setCampos] = useState<CampoDefinicion[]>([])
  const [cargando, setCargando] = useState(true)
  const [guardando, setGuardando] = useState(false)

  const [nuevoPaso, setNuevoPaso] = useState<Record<string, string>>({})
  const [editandoPaso, setEditandoPaso] = useState<{ id: number; nombre: string; color: string | null } | null>(null)
  const [editandoCampo, setEditandoCampo] = useState<{ id: number; etiqueta: string; opciones: string } | null>(null)
  const [campoNuevo, setCampoNuevo] = useState({ etiqueta: '', tipo: 'texto' as TipoCampo, opciones: '' })

  const cargar = useCallback(() => {
    Promise.all([
      api.get<PipelineSubestado[]>('/api/pipeline/subestados').catch(() => [] as PipelineSubestado[]),
      api.get<CampoDefinicion[]>('/api/pipeline/campos').catch(() => [] as CampoDefinicion[]),
    ]).then(([p, c]) => {
      setPasos(Array.isArray(p) ? p : [])
      setCampos(Array.isArray(c) ? c : [])
    }).finally(() => setCargando(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => { cargar() }, [cargar])

  async function conAviso(accion: () => Promise<unknown>) {
    setGuardando(true)
    try {
      await accion()
      cargar()
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'No se pudo guardar', 'error')
    } finally {
      setGuardando(false)
    }
  }

  // ─── Pasos ───

  function agregarPaso(etapa: string) {
    const nombre = (nuevoPaso[etapa] || '').trim()
    if (!nombre) return
    const hermanos = pasos.filter(p => p.etapa === etapa)
    conAviso(async () => {
      await api.post('/api/pipeline/subestados', { etapa, nombre, orden: hermanos.length })
      setNuevoPaso(prev => ({ ...prev, [etapa]: '' }))
    })
  }

  function guardarPaso() {
    if (!editandoPaso) return
    const { id, nombre, color } = editandoPaso
    if (!nombre.trim()) return
    conAviso(async () => {
      await api.put(`/api/pipeline/subestados/${id}`, { nombre: nombre.trim(), color })
      setEditandoPaso(null)
    })
  }

  /* Reordenar es intercambiar con el vecino y mandar los dos: el backend
     recibe pares {id, orden}, así que un movimiento son dos filas. */
  function moverPaso(p: PipelineSubestado, direccion: -1 | 1) {
    const hermanos = pasos.filter(x => x.etapa === p.etapa)
    const i = hermanos.findIndex(x => x.id === p.id)
    const j = i + direccion
    if (j < 0 || j >= hermanos.length) return
    conAviso(() => api.put('/api/pipeline/subestados/orden', {
      items: [{ id: hermanos[i].id, orden: j }, { id: hermanos[j].id, orden: i }],
    }))
  }

  function borrarPaso(p: PipelineSubestado) {
    if (!confirm(`¿Borrar el paso «${p.nombre}»? Las oportunidades que estén ahí lo conservan, marcado como fuera de lista.`)) return
    conAviso(() => api.del(`/api/pipeline/subestados/${p.id}`))
  }

  // ─── Campos ───

  function agregarCampo() {
    const etiqueta = campoNuevo.etiqueta.trim()
    if (!etiqueta) return
    const opciones = campoNuevo.opciones.split(',').map(o => o.trim()).filter(Boolean)
    if (campoNuevo.tipo === 'lista' && opciones.length === 0) {
      showToast('Una lista necesita al menos una opción', 'error')
      return
    }
    conAviso(async () => {
      await api.post('/api/pipeline/campos', {
        etiqueta,
        tipo: campoNuevo.tipo,
        opciones: campoNuevo.tipo === 'lista' ? opciones : undefined,
        orden: campos.length,
      })
      setCampoNuevo({ etiqueta: '', tipo: 'texto', opciones: '' })
    })
  }

  function guardarCampo() {
    if (!editandoCampo) return
    const def = campos.find(c => c.id === editandoCampo.id)
    if (!def || !editandoCampo.etiqueta.trim()) return
    const opciones = editandoCampo.opciones.split(',').map(o => o.trim()).filter(Boolean)
    if (def.tipo === 'lista' && opciones.length === 0) {
      showToast('Una lista necesita al menos una opción', 'error')
      return
    }
    conAviso(async () => {
      await api.put(`/api/pipeline/campos/${def.id}`, {
        etiqueta: editandoCampo.etiqueta.trim(),
        ...(def.tipo === 'lista' ? { opciones } : {}),
      })
      setEditandoCampo(null)
    })
  }

  function borrarCampo(c: CampoDefinicion) {
    if (!confirm(`¿Quitar el campo «${c.etiqueta}» de la ficha?`)) return
    conAviso(async () => {
      const r = await api.del<{ desactivado?: boolean; con_datos?: number }>(`/api/pipeline/campos/${c.id}`)
      if (r?.desactivado) {
        showToast(`«${c.etiqueta}» tiene datos en ${r.con_datos} oportunidades: se ocultó en vez de borrarse.`, 'info')
      }
    })
  }

  if (cargando) return <div className="empty-state"><p>Cargando...</p></div>

  return (
    <div className="card pp-card">
      <div className="pp-card__head">
        <div>
          <h3 className="pp-card__titulo">Pipeline</h3>
          <p className="pp-card__sub">
            Las seis etapas son fijas: de ellas dependen las cotizaciones, los avisos
            automáticos y los reportes. Lo que armas acá es cómo trabajas adentro de cada una.
          </p>
        </div>
      </div>

      <div className="cfg-pestanas">
        <button
          type="button"
          className={`cfg-pestana${pestana === 'pasos' ? ' is-activa' : ''}`}
          onClick={() => setPestana('pasos')}
        >
          Pasos <span className="cfg-pestana__conteo">{pasos.length}</span>
        </button>
        <button
          type="button"
          className={`cfg-pestana${pestana === 'campos' ? ' is-activa' : ''}`}
          onClick={() => setPestana('campos')}
        >
          Campos de la ficha <span className="cfg-pestana__conteo">{campos.length}</span>
        </button>
      </div>

      {/* ─── Pasos ─── */}
      {pestana === 'pasos' && (
        <div className="cfg-etapas">
          {ETAPAS_TABLERO.map(etapa => {
            const suyos = pasos.filter(p => p.etapa === etapa.key)
            return (
              <div key={etapa.key} className="cfg-etapa">
                <div className="cfg-etapa__cabecera">
                  <strong>{etapa.label}</strong>
                  <span className="cfg-etapa__conteo">
                    {suyos.length === 0 ? 'sin pasos' : `${suyos.length} paso${suyos.length === 1 ? '' : 's'}`}
                  </span>
                </div>

                <ul className="cfg-pasos">
                  {suyos.map((p, i) => {
                    if (editandoPaso?.id === p.id) {
                      return (
                        <li key={p.id} className="cfg-paso cfg-paso--editando">
                          <input
                            className="form-input cfg-paso__nombre"
                            value={editandoPaso.nombre}
                            onChange={e => setEditandoPaso({ ...editandoPaso, nombre: e.target.value })}
                            onKeyDown={e => {
                              if (e.key === 'Enter') guardarPaso()
                              if (e.key === 'Escape') setEditandoPaso(null)
                            }}
                            autoFocus
                            disabled={guardando}
                          />
                          <Paleta
                            valor={editandoPaso.color}
                            onElegir={c => setEditandoPaso({ ...editandoPaso, color: c })}
                          />
                          <div className="cfg-paso__acciones">
                            <button type="button" className="cfg-icono" title="Guardar" onClick={guardarPaso} disabled={guardando}>
                              <IconCheck size={13} />
                            </button>
                            <button type="button" className="cfg-icono" title="Cancelar" onClick={() => setEditandoPaso(null)} disabled={guardando}>
                              <IconX size={13} />
                            </button>
                          </div>
                        </li>
                      )
                    }
                    return (
                      <li key={p.id} className={`cfg-paso${p.activo ? '' : ' is-oculto'}`}>
                        {/* El nombre completo en el título: en una columna
                            angosta el chip recorta, y «Calificado para reu…»
                            no dice cuál de dos pasos parecidos es. */}
                        <span className="cfg-chip" title={p.nombre}>
                          <span className="cfg-chip__punto" style={{ background: `var(--${p.color || 'cat-8'})` }} />
                          {p.nombre}
                        </span>
                        <div className="cfg-paso__acciones">
                          <button type="button" className="cfg-icono" title="Editar"
                            onClick={() => setEditandoPaso({ id: p.id, nombre: p.nombre, color: p.color ?? null })}
                            disabled={guardando}>
                            <IconEdit size={13} />
                          </button>
                          <button type="button" className="cfg-icono" title="Subir"
                            onClick={() => moverPaso(p, -1)} disabled={guardando || i === 0}>
                            <IconArrowUp size={13} />
                          </button>
                          <button type="button" className="cfg-icono" title="Bajar"
                            onClick={() => moverPaso(p, 1)} disabled={guardando || i === suyos.length - 1}>
                            <IconArrowDown size={13} />
                          </button>
                          <button type="button" className="cfg-icono" title={p.activo ? 'Dejar de ofrecerlo' : 'Volver a ofrecerlo'}
                            onClick={() => conAviso(() => api.put(`/api/pipeline/subestados/${p.id}`, { activo: !p.activo }))}
                            disabled={guardando}>
                            {p.activo ? <IconEye size={13} /> : <IconEyeOff size={13} />}
                          </button>
                          <button type="button" className="cfg-icono cfg-icono--peligro" title="Borrar"
                            onClick={() => borrarPaso(p)} disabled={guardando}>
                            <IconTrash size={13} />
                          </button>
                        </div>
                      </li>
                    )
                  })}
                </ul>

                <div className="cfg-agregar">
                  <input
                    className="form-input"
                    placeholder="Agregar un paso..."
                    value={nuevoPaso[etapa.key] || ''}
                    onChange={e => setNuevoPaso(prev => ({ ...prev, [etapa.key]: e.target.value }))}
                    onKeyDown={e => { if (e.key === 'Enter') agregarPaso(etapa.key) }}
                    disabled={guardando}
                  />
                  <button type="button" className="btn btn-sm btn-secondary"
                    onClick={() => agregarPaso(etapa.key)} disabled={guardando}>
                    <IconPlus size={13} />
                  </button>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* ─── Campos ─── */}
      {pestana === 'campos' && (
        <>
          <p className="cfg-nota cfg-nota--arriba">
            Los datos que tu embudo usa y el panel todavía no muestra. Aparecen en la ficha de
            cada oportunidad, debajo de los demás.
          </p>

          {campos.length > 0 && (
            <ul className="cfg-campos">
              {campos.map(c => {
                if (editandoCampo?.id === c.id) {
                  return (
                    <li key={c.id} className="cfg-campo cfg-campo--editando">
                      <input
                        className="form-input"
                        value={editandoCampo.etiqueta}
                        onChange={e => setEditandoCampo({ ...editandoCampo, etiqueta: e.target.value })}
                        placeholder="Nombre del campo"
                        autoFocus
                        disabled={guardando}
                      />
                      {c.tipo === 'lista' && (
                        <input
                          className="form-input"
                          value={editandoCampo.opciones}
                          onChange={e => setEditandoCampo({ ...editandoCampo, opciones: e.target.value })}
                          placeholder="Opciones separadas por coma"
                          disabled={guardando}
                        />
                      )}
                      <button type="button" className="cfg-icono" title="Guardar" onClick={guardarCampo} disabled={guardando}>
                        <IconCheck size={13} />
                      </button>
                      <button type="button" className="cfg-icono" title="Cancelar" onClick={() => setEditandoCampo(null)} disabled={guardando}>
                        <IconX size={13} />
                      </button>
                    </li>
                  )
                }
                return (
                  <li key={c.id} className={`cfg-campo${c.activo ? '' : ' is-oculto'}`}>
                    <span className="cfg-campo__etiqueta">{c.etiqueta}</span>
                    <span className="cfg-campo__tipo">
                      {TIPOS.find(t => t.valor === c.tipo)?.label || c.tipo}
                      {c.tipo === 'lista' && c.opciones ? `: ${c.opciones.join(' · ')}` : ''}
                    </span>
                    {!c.activo && <span className="cfg-campo__oculto">oculto</span>}
                    <button type="button" className="cfg-icono" title="Editar"
                      onClick={() => setEditandoCampo({
                        id: c.id,
                        etiqueta: c.etiqueta,
                        opciones: (c.opciones || []).join(', '),
                      })}
                      disabled={guardando}>
                      <IconEdit size={13} />
                    </button>
                    <button type="button" className="cfg-icono cfg-icono--peligro" title="Quitar"
                      onClick={() => borrarCampo(c)} disabled={guardando}>
                      <IconTrash size={13} />
                    </button>
                  </li>
                )
              })}
            </ul>
          )}

          <div className="cfg-campo-nuevo">
            <input
              className="form-input"
              placeholder="Nombre del campo (ej: Temperatura)"
              value={campoNuevo.etiqueta}
              onChange={e => setCampoNuevo(prev => ({ ...prev, etiqueta: e.target.value }))}
              disabled={guardando}
            />
            <select
              className="form-input"
              value={campoNuevo.tipo}
              onChange={e => setCampoNuevo(prev => ({ ...prev, tipo: e.target.value as TipoCampo }))}
              disabled={guardando}
            >
              {TIPOS.map(t => <option key={t.valor} value={t.valor}>{t.label}</option>)}
            </select>
            {campoNuevo.tipo === 'lista' && (
              <input
                className="form-input"
                placeholder="Opciones separadas por coma"
                value={campoNuevo.opciones}
                onChange={e => setCampoNuevo(prev => ({ ...prev, opciones: e.target.value }))}
                disabled={guardando}
              />
            )}
            <button type="button" className="btn btn-primary btn-sm" onClick={agregarCampo} disabled={guardando}>
              <IconPlus size={13} /> Agregar
            </button>
          </div>
          <p className="cfg-nota">
            El tipo de un campo no se cambia después de crearlo: decide cómo se guardó cada
            valor que ya está adentro. El nombre y las opciones sí.
          </p>
        </>
      )}
    </div>
  )
}
